import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

interface SiteNavProps {
  showLoginButton?: boolean;
}

export function SiteNav({ showLoginButton = true }: SiteNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <Link href="/">
            <img 
              src="/img/logo.webp" 
              alt="LandPartner Logo" 
              width={280}
              height={175}
              loading="eager"
              decoding="async"
              className="cursor-pointer flex-shrink-0"
              style={{ height: 'clamp(4rem, 6vw, 6rem)', width: 'auto', minHeight: '4rem' }}
              data-testid="logo-link"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          <Link href="/auth?tab=signup">
            <button className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium whitespace-nowrap" data-testid="nav-submit-deal">
              Submit Deal
            </button>
          </Link>
          <a href="/#what-you-get" className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium whitespace-nowrap" data-testid="nav-how-it-works">
            How It Works
          </a>
          <Link href="/contact">
            <button className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium whitespace-nowrap" data-testid="nav-contact">
              Contact
            </button>
          </Link>
          {showLoginButton && (
            <Link href="/auth?tab=login">
              <Button variant="outline" size="sm" data-testid="link-login">Login</Button>
            </Link>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="flex md:hidden items-center gap-3">
          {showLoginButton && (
            <Link href="/auth?tab=login">
              <Button variant="outline" size="sm" data-testid="link-login-mobile">Login</Button>
            </Link>
          )}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="button-mobile-menu" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                <Link href="/auth?tab=signup" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2" data-testid="nav-submit-deal-mobile">
                    Submit Deal
                  </button>
                </Link>
                <a href="/#what-you-get" onClick={() => setMobileMenuOpen(false)} className="text-foreground hover:text-primary transition-colors font-medium py-2" data-testid="nav-how-it-works-mobile">
                  How It Works
                </a>
                <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2" data-testid="nav-contact-mobile">
                    Contact
                  </button>
                </Link>
                <Link href="/privacy" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2" data-testid="nav-privacy-mobile">
                    Privacy Policy
                  </button>
                </Link>
                <Link href="/terms" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2" data-testid="nav-terms-mobile">
                    Terms & Conditions
                  </button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
